import { useCallback, useEffect } from 'react';
import { posthog } from '../config/posthog';

// Custom hook for analytics tracking
export const useAnalytics = () => {
  // Generic event tracking
  const trackEvent = useCallback((eventName, properties = {}) => {
    if (typeof window !== 'undefined' && posthog) {
      posthog.capture(eventName, {
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        ...properties,
      });
    }
  }, []);

  // Page view tracking
  const trackPageView = useCallback((pageName, properties = {}) => {
    if (typeof window !== 'undefined' && posthog) {
      const referrer = document.referrer;
      const searchEngines = ['google', 'bing', 'duckduckgo', 'yahoo', 'baidu', 'yandex'];
      const isOrganic = referrer && searchEngines.some(engine => referrer.includes(engine));

      posthog.capture('$pageview', {
        page_name: pageName,
        page_url: window.location.href,
        page_path: window.location.pathname,
        referrer: referrer || 'direct',
        traffic_source: isOrganic ? 'organic' : 'other',
        ...properties,
      });
    }
  }, []);

  // App Store download tracking
  const trackAppStoreClick = useCallback((platform, properties = {}) => {
    trackEvent('app_store_click', {
      platform, // 'ios' or 'android'
      ...properties,
    });
  }, [trackEvent]);

  // Modal interaction tracking
  const trackModalInteraction = useCallback((action, modalType, properties = {}) => {
    trackEvent('modal_interaction', {
      action, // 'open', 'close', 'join_testing_group', 'download_beta'
      modal_type: modalType, // 'android_testing'
      ...properties,
    });
  }, [trackEvent]);

  // Navigation tracking
  const trackNavigation = useCallback((destination, method = 'click', properties = {}) => {
    trackEvent('navigation', {
      destination, // 'about', 'faq', 'home', etc.
      method, // 'click', 'direct', 'back_button'
      ...properties,
    });
  }, [trackEvent]);

  // Review interaction tracking
  const trackReviewInteraction = useCallback((action, reviewId = null, properties = {}) => {
    trackEvent('review_interaction', {
      action, // 'view', 'click'
      review_id: reviewId,
      ...properties,
    });
  }, [trackEvent]);

  // Footer link tracking
  const trackFooterLink = useCallback((linkType, destination, properties = {}) => {
    trackEvent('footer_link_click', {
      link_type: linkType, // 'privacy', 'email', 'social'
      destination,
      ...properties,
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackAppStoreClick,
    trackModalInteraction,
    trackNavigation,
    trackReviewInteraction,
    trackFooterLink,
  };
};

// Hook for automatic page view tracking
export const usePageTracking = (pageName, additionalProperties = {}) => {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView(pageName, additionalProperties);
  }, [pageName, trackPageView, additionalProperties]);
}; 